import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { AzureVoice } from "@models/textToSpeech/textToSpeech.model";
import { Project, ProjectsDto } from "@models/textToSpeech/project.model";
import {
  EmotionCharacter,
  EmotionProjectReply,
  ProjectService,
} from "~/app/textToSpeech/project.service";
import { TextToSpeechService } from "~/app/textToSpeech/textToSpeech.service";

@Component({
  selector: "app-emotion-characters",
  templateUrl: "./emotion-characters.component.html",
  styleUrls: ["./emotion-characters.component.scss"],
})
export class EmotionCharactersComponent implements OnInit, OnDestroy {
  public projectId: number;
  public spinner = false;
  public characters: EmotionCharacter[] = [];
  public loadFailed = false;
  public project: Project | null = null;
  public voicesLoading = false;
  public voicesLoadFailed = false;
  public elevenLabsVoices: AzureVoice[] = [];
  public selectedElevenLabsVoice: AzureVoice | null = null;
  public selectedLanguageFilter = "All";
  public selectedGenderFilter = "All";
  public selectedAgeFilter = "All";
  public selectedStyleFilter = "All";
  public playbackFailed = false;
  public voiceSampleLoading = false;
  public voiceSampleFailed = false;
  public associationSaving = false;
  public associationError = false;
  public associationSuccess = false;

  public selectedCharacter: EmotionCharacter | null = null;
  public dialogues: EmotionProjectReply[] = [];
  public dialoguesLoading = false;
  public dialoguesError = false;
  private currentAudio: HTMLAudioElement | null = null;
  private currentlyPlayingKey: string | null = null;
  private currentAudioTimeUpdateHandler: (() => void) | null = null;
  private currentAudioEndedHandler: (() => void) | null = null;
  private currentAudioErrorHandler: (() => void) | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly projectService: ProjectService,
    private readonly textToSpeechService: TextToSpeechService,
    private readonly cdk: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.projectId = Number(this.route.snapshot.paramMap.get("id"));
    if (!this.projectId) {
      this.router.navigate(["/projects"]);
      return;
    }
    this.loadProjectContext();
  }

  ngOnDestroy(): void {
    this.stopCurrentAudio();
  }

  public backToProjects(): void {
    this.router.navigate(["/projects"]);
  }

  public onCharacterClick(character: EmotionCharacter): void {
    this.selectedCharacter = character;
    this.associationError = false;
    this.associationSuccess = false;
    this.dialogues = [];
    this.dialoguesLoading = true;
    this.dialoguesError = false;

    this.projectService
      .getEmotionReplies(this.projectId, character.characterId)
      .subscribe({
        next: (dialogues) => {
          this.dialogues = dialogues;
          this.syncSelectedVoiceWithCharacter();
          this.dialoguesLoading = false;
        },
        error: () => {
          this.dialoguesLoading = false;
          this.dialoguesError = true;
        },
      });
  }

  public onElevenLabsVoiceChange(voiceShortName: string): void {
    this.selectedElevenLabsVoice =
      this.elevenLabsVoices.find(
        (voice) => voice.shortName === voiceShortName
      ) || null;
  }

  public onVoiceFilterChange(): void {
    if (!this.filteredElevenLabsVoices.length) {
      this.selectedElevenLabsVoice = null;
      return;
    }
    const selectedVoiceStillVisible = this.filteredElevenLabsVoices.some(
      (voice) => voice.shortName === this.selectedElevenLabsVoice?.shortName
    );
    if (!selectedVoiceStillVisible) {
      this.selectedElevenLabsVoice = this.filteredElevenLabsVoices[0];
    }
  }

  public get filteredElevenLabsVoices(): AzureVoice[] {
    return this.elevenLabsVoices.filter((voice) => {
      if (
        this.selectedLanguageFilter !== "All" &&
        this.getVoiceLanguage(voice) !== this.selectedLanguageFilter
      ) {
        return false;
      }
      if (
        this.selectedGenderFilter !== "All" &&
        (voice.gender || "").toLowerCase() !==
          this.selectedGenderFilter.toLowerCase()
      ) {
        return false;
      }
      if (
        this.selectedAgeFilter !== "All" &&
        this.getVoiceAge(voice) !== this.selectedAgeFilter
      ) {
        return false;
      }
      if (
        this.selectedStyleFilter !== "All" &&
        !this.getVoiceStyles(voice).includes(this.selectedStyleFilter)
      ) {
        return false;
      }
      return true;
    });
  }

  public get languageFilterOptions(): string[] {
    const languages = Array.from(
      new Set(
        this.elevenLabsVoices.map((voice) => this.getVoiceLanguage(voice))
      )
    ).filter(Boolean);
    return ["All", ...languages.sort()];
  }

  public get genderFilterOptions(): string[] {
    const genders = Array.from(
      new Set(
        this.elevenLabsVoices
          .map((voice) => voice.gender)
          .filter((gender) => !!gender)
          .map((gender) => this.capitalize(gender as string))
      )
    );
    return ["All", ...genders.sort()];
  }

  public get ageFilterOptions(): string[] {
    const ages = Array.from(
      new Set(this.elevenLabsVoices.map((voice) => this.getVoiceAge(voice)))
    ).filter(Boolean);
    return ["All", ...ages.sort()];
  }

  public get styleFilterOptions(): string[] {
    const allStyles = this.elevenLabsVoices.reduce(
      (acc: string[], voice: AzureVoice) =>
        acc.concat(this.getVoiceStyles(voice)),
      []
    );
    const styles = Array.from(new Set(allStyles)).filter((style) => !!style);
    return ["All", ...styles.sort()];
  }

  public onDialoguePlayRequested(
    line: EmotionProjectReply,
    index: number
  ): void {
    this.playRange(line.startSec, line.endSec, `dialogue-${index}`);
  }

  public onPlaySelectedVoiceSample(): void {
    this.voiceSampleFailed = false;
    if (!this.project || !this.selectedElevenLabsVoice) {
      return;
    }

    if (this.isVoiceSamplePlaying) {
      this.stopCurrentAudio();
      this.cdk.detectChanges();
      return;
    }

    this.stopCurrentAudio();
    this.voiceSampleLoading = true;

    const previewRequest = this.buildVoicePreviewRequest(
      this.getVoiceSampleText()
    );
    this.projectService.createPreviewAudio(previewRequest).subscribe({
      next: (audioPath: string) => {
        this.voiceSampleLoading = false;
        this.currentlyPlayingKey = "voice-sample";
        this.currentAudio = new Audio(audioPath);
        this.currentAudioTimeUpdateHandler = null;
        this.currentAudioEndedHandler = () => {
          this.stopCurrentAudio();
          this.cdk.detectChanges();
        };
        this.currentAudioErrorHandler = () => {
          this.voiceSampleFailed = true;
          this.stopCurrentAudio();
          this.cdk.detectChanges();
        };
        this.currentAudio.addEventListener(
          "ended",
          this.currentAudioEndedHandler
        );
        this.currentAudio.addEventListener(
          "error",
          this.currentAudioErrorHandler
        );
        this.currentAudio.play().catch(() => {
          this.voiceSampleFailed = true;
          this.stopCurrentAudio();
          this.cdk.detectChanges();
        });
      },
      error: () => {
        this.voiceSampleLoading = false;
        this.voiceSampleFailed = true;
      },
    });
  }

  public onCharacterPlay(character: EmotionCharacter, event: MouseEvent): void {
    event.stopPropagation();
    this.playbackFailed = false;

    const isSelectedCharacter =
      this.selectedCharacter?.characterId === character.characterId;
    if (isSelectedCharacter && this.dialogues?.length) {
      this.playRange(
        this.dialogues[0].startSec,
        this.dialogues[0].endSec,
        `character-${character.characterId}`
      );
      return;
    }

    this.selectedCharacter = character;
    this.associationError = false;
    this.associationSuccess = false;
    this.dialogues = [];
    this.dialoguesLoading = true;
    this.dialoguesError = false;
    this.projectService
      .getEmotionReplies(this.projectId, character.characterId)
      .subscribe({
        next: (dialogues) => {
          this.dialogues = dialogues;
          this.syncSelectedVoiceWithCharacter();
          this.dialoguesLoading = false;
          if (this.dialogues.length) {
            this.playRange(
              this.dialogues[0].startSec,
              this.dialogues[0].endSec,
              `character-${character.characterId}`
            );
          }
        },
        error: () => {
          this.dialoguesLoading = false;
          this.dialoguesError = true;
          this.playbackFailed = true;
        },
      });
  }

  public isDialoguePlaying(index: number): boolean {
    return this.currentlyPlayingKey === `dialogue-${index}`;
  }

  public isCharacterPlaying(character: EmotionCharacter): boolean {
    return this.currentlyPlayingKey === `character-${character.characterId}`;
  }

  public get canPlayOriginalAudio(): boolean {
    return !!this.originalAudioSourcePath;
  }

  public get canAssociateVoice(): boolean {
    return !!(
      this.selectedCharacter &&
      this.selectedElevenLabsVoice &&
      !this.associationSaving
    );
  }

  public associateVoiceToCharacter(): void {
    if (!this.selectedCharacter || !this.selectedElevenLabsVoice) {
      return;
    }

    this.associationSaving = true;
    this.associationError = false;
    this.associationSuccess = false;

    this.projectService
      .associateEmotionVoice(
        this.projectId,
        this.selectedCharacter.characterId,
        this.selectedElevenLabsVoice.shortName
      )
      .subscribe({
        next: (response) => {
          this.associationSaving = false;
          this.associationSuccess = response?.isSuccess === true;
          this.associationError = !this.associationSuccess;
          if (this.associationSuccess) {
            this.selectedCharacter = {
              ...this.selectedCharacter!,
              voiceId: this.selectedElevenLabsVoice!.shortName,
            };
            this.characters = this.characters.map((char) =>
              char.characterId === this.selectedCharacter!.characterId
                ? { ...char, voiceId: this.selectedElevenLabsVoice!.shortName }
                : char
            );
          }
        },
        error: () => {
          this.associationSaving = false;
          this.associationError = true;
          this.associationSuccess = false;
        },
      });
  }

  public getCharacterVoiceLabel(character: EmotionCharacter): string | null {
    const voiceId = `${character?.voiceId || ""}`.trim();
    if (!voiceId) {
      return null;
    }

    const matchedVoice = this.elevenLabsVoices.find(
      (voice) => voice.shortName === voiceId
    );

    if (!matchedVoice) {
      return voiceId;
    }

    return (
      matchedVoice.maskedShortName ||
      matchedVoice.displayName ||
      matchedVoice.shortName
    );
  }

  public get canPlayVoiceSample(): boolean {
    return !!(
      this.project &&
      this.selectedElevenLabsVoice &&
      !this.voiceSampleLoading
    );
  }

  public get isVoiceSamplePlaying(): boolean {
    return this.currentlyPlayingKey === "voice-sample";
  }

  private loadProjectContext(): void {
    this.projectService.getById(this.projectId).subscribe({
      next: (project) => {
        this.project = project;
        const emotionVoiceLanguageRegion =
          this.resolveEmotionVoiceLanguageRegion(project);
        console.warn("[EmotionCharacters] loadProjectContext", {
          projectId: project?.id,
          language: project?.language,
          region: project?.region,
          targetTranslationLanguage: project?.targetTranslationLanguage,
          emotionVoiceLanguageRegion,
        });
        this.loadElevenLabsVoices(emotionVoiceLanguageRegion, project.voice);
        this.loadCharacters();
      },
      error: () => {
        this.project = null;
        this.loadFailed = true;
        this.voicesLoadFailed = true;
      },
    });
  }

  private loadElevenLabsVoices(
    languageRegion: string,
    projectVoice?: string
  ): void {
    console.warn("[EmotionCharacters] loadElevenLabsVoices:start", {
      languageRegion,
      projectVoice,
      targetTranslationLanguage: this.project?.targetTranslationLanguage,
    });
    this.voicesLoading = true;
    this.voicesLoadFailed = false;
    this.elevenLabsVoices = [];
    this.selectedElevenLabsVoice = null;

    this.textToSpeechService
      .getVoicesByLanguageAndRegion(languageRegion, "ElevenLabs")
      .subscribe({
        next: (voices) => {
          this.elevenLabsVoices = (voices || []).filter(
            (voice) => voice.createdBy === "ElevenLabs"
          );
          console.warn("[EmotionCharacters] loadElevenLabsVoices:response", {
            requestedLanguageRegion: languageRegion,
            totalVoicesReturned: (voices || []).length,
            elevenLabsVoicesCount: this.elevenLabsVoices.length,
            voiceLocales: this.elevenLabsVoices.map((voice) => voice.locale),
          });
          this.resetVoiceFilters();
          this.applyTargetTranslationLanguageFilter();
          const currentProjectVoiceShortName = (projectVoice || "").split(
            "/"
          )[0];
          this.selectedElevenLabsVoice =
            this.elevenLabsVoices.find(
              (voice) => voice.shortName === currentProjectVoiceShortName
            ) ||
            this.elevenLabsVoices[0] ||
            null;
          this.onVoiceFilterChange();
          console.warn("[EmotionCharacters] loadElevenLabsVoices:selection", {
            selectedLanguageFilter: this.selectedLanguageFilter,
            languageFilterOptions: this.languageFilterOptions,
            filteredVoicesCount: this.filteredElevenLabsVoices.length,
            selectedVoice: this.selectedElevenLabsVoice?.shortName || null,
          });
          this.syncSelectedVoiceWithCharacter();
        },
        error: () => {
          this.elevenLabsVoices = [];
          this.selectedElevenLabsVoice = null;
          this.voicesLoadFailed = true;
        },
      })
      .add(() => {
        this.voicesLoading = false;
      });
  }

  private playRange(
    startSecValue: number,
    endSecValue: number,
    playingKey: string
  ): void {
    this.playbackFailed = false;

    const sourcePath = this.originalAudioSourcePath;
    if (!sourcePath) {
      this.playbackFailed = true;
      return;
    }

    const startSec = Number(startSecValue);
    const endSec = Number(endSecValue);
    if (
      !Number.isFinite(startSec) ||
      !Number.isFinite(endSec) ||
      endSec <= startSec
    ) {
      this.playbackFailed = true;
      return;
    }

    if (this.currentlyPlayingKey === playingKey && this.currentAudio) {
      this.stopCurrentAudio();
      this.cdk.detectChanges();
      return;
    }

    this.stopCurrentAudio();
    this.currentlyPlayingKey = playingKey;
    this.currentAudio = new Audio(sourcePath);

    this.currentAudioTimeUpdateHandler = () => {
      if (!this.currentAudio) {
        return;
      }
      if (this.currentAudio.currentTime >= endSec) {
        this.stopCurrentAudio();
        this.cdk.detectChanges();
      }
    };
    this.currentAudioEndedHandler = () => {
      this.stopCurrentAudio();
      this.cdk.detectChanges();
    };
    this.currentAudioErrorHandler = () => {
      this.playbackFailed = true;
      this.stopCurrentAudio();
      this.cdk.detectChanges();
    };

    this.currentAudio.addEventListener(
      "timeupdate",
      this.currentAudioTimeUpdateHandler
    );
    this.currentAudio.addEventListener("ended", this.currentAudioEndedHandler);
    this.currentAudio.addEventListener("error", this.currentAudioErrorHandler);

    this.currentAudio.currentTime = Math.max(0, startSec);
    this.currentAudio.play().catch(() => {
      this.playbackFailed = true;
      this.stopCurrentAudio();
      this.cdk.detectChanges();
    });
  }

  private stopCurrentAudio(): void {
    if (this.currentAudio) {
      if (this.currentAudioTimeUpdateHandler) {
        this.currentAudio.removeEventListener(
          "timeupdate",
          this.currentAudioTimeUpdateHandler
        );
      }
      if (this.currentAudioEndedHandler) {
        this.currentAudio.removeEventListener(
          "ended",
          this.currentAudioEndedHandler
        );
      }
      if (this.currentAudioErrorHandler) {
        this.currentAudio.removeEventListener(
          "error",
          this.currentAudioErrorHandler
        );
      }
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    this.currentAudioTimeUpdateHandler = null;
    this.currentAudioEndedHandler = null;
    this.currentAudioErrorHandler = null;
    this.currentlyPlayingKey = null;
  }

  private buildVoicePreviewRequest(dialogueText: string): ProjectsDto {
    const voice = this.selectedElevenLabsVoice as AzureVoice;
    const project = this.project as Project;
    return {
      id: project.id,
      userId: project.userId,
      projectNames: [project.name || ""],
      uniqueProjectNames: [project.uniqueProjectName],
      language: project.language,
      region: project.region,
      voice: `${voice.shortName}/${
        voice.maskedShortName || voice.displayName || voice.shortName
      }/ElevenLabs/`,
      lexiconUri: project.lexiconUri || " ",
      startTimeCode: project.startTimeCode || "00:00:00:00",
      speedRate: project.speedRate || 1,
      usersToNotify: [],
      isRestarting: false,
      isAutomatedSpeed: false,
      automatedSpeedRate: project.automatedSpeedRate || 1,
      previewAudio: dialogueText,
      voiceSetting: "synthetic",
    };
  }

  private getVoiceSampleText(): string {
    const firstDialogue = `${this.dialogues?.[0]?.dialogue || ""}`.trim();
    if (firstDialogue) {
      return firstDialogue;
    }
    return "This is a sample preview for the selected ElevenLabs voice.";
  }

  private get originalAudioSourcePath(): string | null {
    const stereoPath = `${this.project?.stereoFilePath || ""}`.trim();
    if (stereoPath) {
      return stereoPath;
    }
    const mixedPath = `${this.project?.audioFilePath || ""}`.trim();
    return mixedPath || null;
  }

  private getVoiceLanguage(voice: AzureVoice): string {
    return (voice.locale || "").split("-")[0]?.toUpperCase() || "Unknown";
  }

  private getVoiceStyles(voice: AzureVoice): string[] {
    if (!voice.styleList) {
      return [];
    }
    return voice.styleList
      .split(",")
      .map((style) => style.trim())
      .filter((style) => !!style);
  }

  private getVoiceAge(voice: AzureVoice): string {
    const searchableValue = `${voice.name || ""} ${voice.displayName || ""} ${
      voice.styleList || ""
    }`.toLowerCase();

    if (searchableValue.includes("child") || searchableValue.includes("kid")) {
      return "Child";
    }
    if (searchableValue.includes("teen")) {
      return "Teen";
    }
    if (searchableValue.includes("young")) {
      return "Young Adult";
    }
    if (searchableValue.includes("middle")) {
      return "Middle-aged";
    }
    if (
      searchableValue.includes("senior") ||
      searchableValue.includes("elder") ||
      searchableValue.includes("old")
    ) {
      return "Senior";
    }
    if (
      searchableValue.includes("adult") ||
      searchableValue.includes("mature")
    ) {
      return "Adult";
    }
    return "Unknown";
  }

  private resetVoiceFilters(): void {
    this.selectedLanguageFilter = "All";
    this.selectedGenderFilter = "All";
    this.selectedAgeFilter = "All";
    this.selectedStyleFilter = "All";
  }

  private applyTargetTranslationLanguageFilter(): void {
    const rawTargetLanguage = `${this.project?.targetTranslationLanguage || ""}`
      .trim()
      .toUpperCase();
    if (!rawTargetLanguage) {
      console.warn(
        "[EmotionCharacters] applyTargetTranslationLanguageFilter:skip",
        { reason: "missing_targetTranslationLanguage" }
      );
      return;
    }

    const targetLanguageCode = rawTargetLanguage.split("-")[0];
    const availableLanguages = this.languageFilterOptions;
    console.warn("[EmotionCharacters] applyTargetTranslationLanguageFilter", {
      rawTargetLanguage,
      targetLanguageCode,
      availableLanguages,
    });
    if (availableLanguages.includes(targetLanguageCode)) {
      this.selectedLanguageFilter = targetLanguageCode;
      console.warn(
        "[EmotionCharacters] applyTargetTranslationLanguageFilter:applied",
        { selectedLanguageFilter: this.selectedLanguageFilter }
      );
      return;
    }
    console.warn(
      "[EmotionCharacters] applyTargetTranslationLanguageFilter:fallback",
      { selectedLanguageFilter: this.selectedLanguageFilter }
    );
  }

  private resolveEmotionVoiceLanguageRegion(project: Project): string {
    const targetTranslationLanguage = `${
      project?.targetTranslationLanguage || ""
    }`
      .trim()
      .toLowerCase();
    const projectLanguage = `${project?.language || ""}`.trim().toLowerCase();
    const projectRegion = `${project?.region || ""}`.trim().toUpperCase();

    if (targetTranslationLanguage) {
      if (targetTranslationLanguage.includes("-")) {
        return targetTranslationLanguage;
      }
      if (projectRegion) {
        return `${targetTranslationLanguage}-${projectRegion}`;
      }
      return targetTranslationLanguage;
    }

    if (projectLanguage && projectRegion) {
      return `${projectLanguage}-${projectRegion}`;
    }
    return `${projectLanguage || "en"}-${projectRegion || "CA"}`;
  }

  private capitalize(value: string): string {
    if (!value) {
      return value;
    }
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }

  private loadCharacters(): void {
    this.spinner = true;
    this.projectService.initEmotionReplies(this.projectId).subscribe({
      next: () => {
        this.projectService
          .getEmotionCharacters(this.projectId)
          .subscribe({
            next: (characters) => {
              this.characters = characters || [];
              this.loadFailed = false;
              this.selectInitialCharacterIfNeeded();
            },
            error: () => {
              this.characters = [];
              this.loadFailed = true;
            },
          })
          .add(() => {
            this.spinner = false;
          });
      },
      error: () => {
        this.characters = [];
        this.loadFailed = true;
        this.spinner = false;
      },
    });
  }

  private syncSelectedVoiceWithCharacter(): void {
    if (!this.selectedCharacter?.voiceId || !this.elevenLabsVoices.length) {
      return;
    }

    const matchedVoice = this.elevenLabsVoices.find(
      (voice) => voice.shortName === this.selectedCharacter?.voiceId
    );
    if (matchedVoice) {
      this.selectedElevenLabsVoice = matchedVoice;
    }
  }

  private selectInitialCharacterIfNeeded(): void {
    if (this.selectedCharacter || !this.characters.length) {
      return;
    }
    this.onCharacterClick(this.characters[0]);
  }
}
